/* eslint-disable no-nested-ternary */
import { Fragment, useEffect, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import ArrowIosDown from '@crehana/icons/outline/ArrowIosDown';
import ArrowIosUp from '@crehana/icons/outline/ArrowIosUp';
import Thunder from '@crehana/icons/outline/Thunder';
import UpTrend from '@crehana/icons/UpTrend';
import {
  Col,
  Collapse,
  Container,
  colors,
  LazyImage,
  Loader,
  PrimaryButton,
  Row,
  // biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
} from '@crehana/ui';
import { useMe } from '@crehana/web/ReactContexts/AuthContext';
import { useRouter } from 'next/router';
import { DefaultSeo } from 'next-seo';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { css } from 'styled-components';

import { initializeApollo } from '@/shared/libs/apollo';
import { getI18nData } from '@/shared/utils/getI18n/getI18n';
import type { CustomGetServerSideProps } from '@/shared/utils/getServerSideProps';

import Header from '../../components/Header';
import { AMPLITUDE_SKILL_XP_PAGE, PAGE_KEY, ROUTES } from '../../constants';
import { withAuth } from '../../hocs';
import withTagsByTrack from '../../hocs/withAuth/withTagsByTrack';
import useSkillXPResults from '../../hooks/useSkillXPResults';
import useSkillXPUserResultByTag from '../../hooks/useSkillXPUserResultByTag';
import localeEn from '../../i18n/localeEn';
import localeEs from '../../i18n/localeEs';
import localePt from '../../i18n/localePt';
import RecommendedCourses from './sections/RecommendedCourses';
import ResultsChart from './sections/ResultsChart';
import SkillsGraph from './sections/SkillsGraph';

const { secondaryMain } = colors;

const Results = () => {
  const { me } = useMe();
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const router = useRouter();
  const { skillB2BUserId, slug, trackOriginalId } = router.query as {
    skillB2BUserId: string;
    slug: string;
    trackOriginalId: string;
  };

  const [currentOpenCollapse, setCurrentOpenCollapse] = useState<number | null>(
    null,
  );

  const {
    level,
    strengths,
    weaknesses,
    courses,
    yAxisScore,
    xAxisScore,
    yAxisPreviousScore,
    xAxisPreviousScore,
    certificateImage,
    certificateDownloadUrl,
    error,
    loading,
    refetch,
  } = useSkillXPResults(Number(skillB2BUserId));

  const {
    graphData,
    error: graphError,
    refetch: refetchGraph,
  } = useSkillXPUserResultByTag({
    trackId: Number(trackOriginalId),
    skillClose: !!yAxisPreviousScore,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    logEvent({
      event: AMPLITUDE_SKILL_XP_PAGE.RENDER_SKILL_XP_RESULT_PAGE,
      data: {
        'Skill XP Track': slug,
        'Skill XP Original ID': trackOriginalId,
        ...getSourceEventParams(),
      },
    });
  }, []);

  const EmptySkills = () => {
    return (
      <div className="flex justify-center items-center rounded-5 bg-base-lighter-16 py-40">
        <p className="font-subtitle4 text-white">Aún no hay resultados</p>
      </div>
    );
  };

  const handleGoBack = () => {
    router.push(`/org/${me?.organization?.slug}/learn/`);
  };

  if (error) {
    return (
      <div className="bg-base-main" style={{ minHeight: '100vh' }}>
        <Header onGoBack={handleGoBack} />
        <Container className="flex flex-col justify-center self-center items-center h-full p-24">
          <p className="font-h3 text-white mb-24">{t('RESULTS_ERROR')}</p>
          <p className="font-body3 text-red-main mb-24">{error.message}</p>
          <PrimaryButton
            label={t('RESULTS_ERROR_CTA')}
            onClick={() => refetch()}
            isDark
          />
        </Container>
      </div>
    );
  }

  return (
    <Fragment>
      <DefaultSeo titleTemplate="%s | Skill XP | Crehana para empresas" />
      <Header onGoBack={handleGoBack} />
      <section
        className="bg-base-main"
        style={{ minHeight: 'calc(100vh - 76px)' }}
      >
        <ResultsChart
          me={me}
          yAxisPreviousScore={yAxisPreviousScore}
          xAxisPreviousScore={xAxisPreviousScore}
          yAxisScore={yAxisScore}
          xAxisScore={xAxisScore}
          level={level}
          slug={slug}
          goBack={() => router.push(ROUTES.BASE)}
        />

        <Container>
          <Row>
            <Col className="p-0">
              <h3 className="font-h3 text-white mb-32">{t('SKILLS_TITLE')}</h3>
            </Col>
          </Row>
          <Row>
            <Col lg={4} md={12} xs={12}>
              <p className="font-subtitle1 text-white pb-24 border-b-1 border-solid border-gray-main-dark-mode">
                {t('RESULTS_ANALYSIS')}
              </p>
              <Row
                gutter={{
                  sm: 16,
                }}
                totalCols={{
                  xs: 12,
                }}
              >
                <Col lg={12} md={5} sm={6}>
                  <Fragment>
                    <div className="flex items-center py-4 pl-12 my-8 border-l-4 border-solid border-secondary-main">
                      <Thunder size={24} color={secondaryMain} />
                      <p className="font-subtitle3 ml-12 text-secondary-main">
                        {t('SKILLS_STRENGTHS')}
                      </p>
                    </div>
                    {strengths.length === 0 ? (
                      <EmptySkills />
                    ) : (
                      strengths.map(strength => (
                        <button
                          type="button"
                          key={strength.originalId}
                          className="py-12 text-white px-16 w-full rounded-5 mb-12 bg-gray-main-dark-mode"
                          onClick={() =>
                            currentOpenCollapse === strength.originalId
                              ? setCurrentOpenCollapse(null)
                              : setCurrentOpenCollapse(
                                  strength.originalId || null,
                                )
                          }
                        >
                          <div className="font-subtitle4 flex justify-between text-left">
                            {strength.name}
                            {currentOpenCollapse === strength.originalId ? (
                              <ArrowIosUp size={20} color={secondaryMain} />
                            ) : (
                              <ArrowIosDown size={20} color={secondaryMain} />
                            )}
                          </div>
                          <Collapse
                            open={currentOpenCollapse === strength.originalId}
                          >
                            <p className="font-body4 pt-16 text-left">
                              {strength.description}
                            </p>
                          </Collapse>
                        </button>
                      ))
                    )}
                  </Fragment>
                </Col>
                <Col lg={12} md={5} sm={6} offset={{ lg: 0, md: 2 }}>
                  <Fragment>
                    <div className="flex items-center py-4 pl-12 my-8 border-l-4 border-solid border-secondary-main">
                      <UpTrend size={24} color={secondaryMain} />
                      <p className="font-subtitle3 ml-12 text-secondary-main">
                        {t('SKILLS_WEAKNESSES')}
                      </p>
                    </div>
                    {weaknesses.length === 0 ? (
                      <EmptySkills />
                    ) : (
                      weaknesses.map(weakness => (
                        <button
                          type="button"
                          key={weakness.originalId}
                          className="py-12 text-white px-16 w-full rounded-5 mb-12 bg-gray-main-dark-mode"
                          onClick={() =>
                            currentOpenCollapse === weakness.originalId
                              ? setCurrentOpenCollapse(null)
                              : setCurrentOpenCollapse(
                                  weakness.originalId || null,
                                )
                          }
                        >
                          <div className="font-subtitle4 flex justify-between text-left">
                            {weakness.name}
                            {currentOpenCollapse === weakness.originalId ? (
                              <ArrowIosUp size={20} color={secondaryMain} />
                            ) : (
                              <ArrowIosDown size={20} color={secondaryMain} />
                            )}
                          </div>
                          <Collapse
                            open={currentOpenCollapse === weakness.originalId}
                          >
                            <p className="font-body4 pt-16 text-left">
                              {weakness.description}
                            </p>
                          </Collapse>
                        </button>
                      ))
                    )}
                  </Fragment>
                </Col>
              </Row>
            </Col>
            <Col offset={{ lg: 1, md: 1 }} lg={7} md={10} xs={12}>
              {loading ? (
                <div className="w-full h-full flex justify-center items-center">
                  <Loader isWhite />
                </div>
              ) : (
                <SkillsGraph
                  refetch={refetchGraph}
                  data={graphData}
                  error={graphError}
                />
              )}
            </Col>
          </Row>
        </Container>

        <Container className="py-80">
          {loading ? (
            <div className="w-full flex justify-center items-center">
              <Loader isWhite />
            </div>
          ) : (
            <Fragment>
              {certificateImage && certificateDownloadUrl ? (
                <Row className="mt-32">
                  <Col lg={12} md={12} sm={12} xs={12}>
                    <p className="font-h3 text-white mb-16 sm:mb-20">
                      {t('RESULTS_CONGRATULATIONS_TITLE1', {
                        interpolation: { name: me?.firstName },
                      })}
                    </p>
                    <p className="font-body1 text-white">
                      {t('RESULTS_CONGRATULATIONS_COPY1')}
                      {slug}
                      {t('RESULTS_CONGRATULATIONS_COPY2')}
                    </p>
                  </Col>
                  <Col xs={12} sm={5} md={5} lg={5}>
                    <PrimaryButton
                      label={t('RESULTS_DOWNLOAD_DIPLOMA')}
                      href={certificateDownloadUrl || ''}
                      target="_blank"
                      isDark
                      className="mb-24 sm:mb-0"
                    />
                  </Col>
                  <Col
                    offset={{ xs: 0, sm: 0, md: 0, lg: 0 }}
                    xs={12}
                    sm={6}
                    md={6}
                    lg={6}
                    css={css`
                      height: 280px;
                      ${props => props.theme.breakpoints.up('lg')} {
                        height: 320px;
                      }
                    `}
                  >
                    <LazyImage
                      className="w-full h-full"
                      imgClassName="object-cover"
                      src={certificateImage || ''}
                      imgxParams={{ w: 375, h: 244 }}
                      alt="diploma"
                    />
                  </Col>
                </Row>
              ) : courses.length > 0 ? (
                <RecommendedCourses courses={courses} />
              ) : (
                <Row>
                  <Col lg={9} md={9} xs={12} offset={{ lg: 2, md: 2 }}>
                    <p className="font-subtitle1 text-white pb-32 text-center">
                      {t('RESULTS_EMPTY_COURSES1')}{' '}
                      <span className="text-secondary-main">{slug}</span>.{' '}
                      {t('RESULTS_EMPTY_COURSES2')}
                    </p>
                  </Col>
                </Row>
              )}
            </Fragment>
          )}
        </Container>
        <div />
      </section>
    </Fragment>
  );
};

// biome-ignore lint/complexity/noBannedTypes: legacy code - complexity/noBannedTypes
export const getServerSideProps: CustomGetServerSideProps<{}> = async ({
  req,
}) => {
  const apolloClient = initializeApollo();
  const i18nData = await getI18nData({
    apolloClient,
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt } },
      en: { [PAGE_KEY]: { ...localeEn.en } },
    },
  });

  return {
    props: {
      ...i18nData,
    },
  };
};

export default withAuth(withTagsByTrack(Results));
